import {
  GridDataStateChangeEvent,
  GridHeaderSelectionChangeEvent,
  GridItemChangeEvent,
  GridPagerSettings,
  GridRowProps,
  GridSelectionChangeEvent,
} from '@progress/kendo-react-grid';
import { GridSelectableSettings } from '@progress/kendo-react-grid/dist/npm/interfaces/GridSelectableSettings';
import { cloneElement, useCallback, useMemo } from 'react';
import { useInvoiceScanManagerColumns } from 'src/assets/static/invoice_scan_manager/invoiceScanManagerColumns';
import GridWrapper from 'src/components/GridWrapper';
import useInvoiceScanManager from 'src/hooks/useInvoiceScanManager';
import useWindowSize from 'src/hooks/useWindowSize';
import GridToolbar from './GridToolBar';
import { initialOcrDataState } from './InvoiceScanManagerContext';

const selectable: GridSelectableSettings = {
  enabled: true,
  drag: false,
  cell: false,
  mode: 'multiple',
};

const gridPagerSettings: GridPagerSettings = {
  buttonCount: 5,
  pageSizes: [10, 20, 50, 100],
};

const rowRender = (trElement: React.ReactElement<HTMLTableRowElement>, props: GridRowProps) => {
  let trProps: any = {};
  trProps = {
    ...trElement.props,
    className: `${trElement.props.className} ${props.className} ${
      props.dataItem.preview ? 'border-preview-box' : ''
    }`,
  };
  return cloneElement(trElement, { ...trProps });
};

const defaultProps = {
  tableTextId: 'Records',
  DataItemKey: 'rowId',
  sortable: true,
  resizable: true,
  showColumnMenu: true,
  selectable: selectable,
  GridPagerSettings: gridPagerSettings,
  rowRender,
} as const;

const InvoiceScanManagerTable = () => {
  const {
    attachmentListData,
    count,
    setAttachmentListData,
    setDataState,
    dataState,
    isAttachDocumentLoading,
    setDropdownFilter,
    invoiceScanManagerFilterInitialState,
    showPrev,
  } = useInvoiceScanManager();

  const { height } = useWindowSize();

  const invoiceScanManagerColumns = useInvoiceScanManagerColumns();

  const onHeaderSelectionChange = useCallback(
    (event: GridHeaderSelectionChangeEvent) => {
      const checkboxElement: any = event.syntheticEvent.target;
      const { checked } = checkboxElement;
      setAttachmentListData((prev) => prev.map((element) => ({ ...element, selected: checked })));
    },
    [setAttachmentListData]
  );

  const onRowSelectionChange = useCallback(
    (event: GridSelectionChangeEvent) => {
      setAttachmentListData((prev) => {
        const prevItems = [...prev];
        const index = event?.startRowIndex;
        prevItems[index].selected = !prevItems[index].selected;
        return prevItems;
      });
    },
    [setAttachmentListData]
  );

  const onScanManagerDataStateChange = useCallback(
    (e: GridDataStateChangeEvent) => {
      setDataState(e.dataState);
    },
    [setDataState]
  );

  const itemChange = useCallback(
    (event: GridItemChangeEvent) => {
      if (event.field === 'originalFile') {
        let { field } = event;
        event.dataItem[field] = event.value;
        setAttachmentListData((prev) =>
          [...prev].map((row: any) => {
            if (row.attachId === event.dataItem.attachId) {
              row[field] = event.value;
            }
            return row;
          })
        );
      }
    },
    [setAttachmentListData]
  );

  const clearFilter = useCallback(() => {
    setDataState(initialOcrDataState);
    setDropdownFilter(invoiceScanManagerFilterInitialState);
  }, [setDataState, setDropdownFilter, invoiceScanManagerFilterInitialState]);

  const gridToolbarChildren = useMemo(
    () => <GridToolbar clearFilter={clearFilter} />,
    [clearFilter]
  );

  return (
    <GridWrapper
      columns={invoiceScanManagerColumns}
      total={count || 0}
      className="min-h-[300px]"
      dataSource={attachmentListData}
      dataState={dataState}
      onHeaderSelectionChange={onHeaderSelectionChange}
      onItemChange={itemChange}
      onSelectionChange={onRowSelectionChange}
      onDataStateChange={onScanManagerDataStateChange}
      pageable={gridPagerSettings}
      height={`${height - 160}px`}
      gridToolbarChildren={gridToolbarChildren}
      fieldModifiable
      loading={isAttachDocumentLoading}
      key={`${showPrev}`}
      {...defaultProps}
    />
  );
};
export default InvoiceScanManagerTable;
